// RITA-1525 — Mehrseitiges Beispieldokument für Seitenansicht, PDF-Probe und Tests.
//
// Der Text ist ein frei formuliertes Muster nach dem Aufbau des Kooperationsvertrags
// (nummerierte Absätze, Unterschriften, Anlage mit Steuer- und Bankangaben). Er ist kein
// Kundentext und keine rechtliche Vorlage; der Kunde gestaltet seine Verträge später selbst.
import {
  absatz,
  BISHERIGE_SCHRIFT,
  dokument,
  kaestchen,
  liste,
  platzhalter,
  seitenumbruch,
  text,
  zeilenumbruch,
} from '../model/erzeugen';
import { Block, HorizontalePosition, Vertragsdokument } from '../model/types';

export interface Beispieloptionen {
  /** id der Vertragsart für das Kopfzeilen-Logo; ohne Angabe kein Logo (die Vertragsarten kommen aus der Datenbank). */
  logo?: number | null;
  logoPosition?: HorizontalePosition;
  seitenzahlAnzeigen?: boolean;
}

const nummer = (zahl: number, titel: string, inhalt: string): Block =>
  absatz([text(`${zahl}. ${titel}`, { fett: true }), zeilenumbruch(), text(inhalt)]);

export function beispielvertrag(optionen: Beispieloptionen = {}): Vertragsdokument {
  return dokument({
    // Gleiche Schrift wie die gespeicherten Vorlagen von vor RITA-1571 (Zeilenhöhe 1.4, Absatzabstand 0.6 em) und
    // wie das Server-Gegenstück beispielvertrag.v2.json; Seitenzahlen der Tests hängen daran.
    schrift: BISHERIGE_SCHRIFT,
    kopf: {
      logo: {
        vertragsartId: optionen.logo ?? null,
        position: optionen.logoPosition ?? 'rechts',
        hoeheMm: 12,
      },
      inhalt: [],
    },
    fuss: {
      inhalt: [absatz([text('Mein Urlaubsglück GmbH • Rotenburger Str. 9 • 30659 Hannover')])],
      seitenzahl: { anzeigen: optionen.seitenzahlAnzeigen ?? true, position: 'rechts' },
    },
    text: [
      absatz([text('KOOPERATIONSVERTRAG', { fett: true })], 'titel'),
      absatz('für selbständige Reiseberater'),
      absatz('zwischen der'),
      absatz([
        text('Mein Urlaubsglück GmbH', { fett: true }),
        zeilenumbruch(),
        text('Rotenburger Str. 9'),
        zeilenumbruch(),
        text('30659 Hannover'),
      ]),
      absatz('nachfolgend „MUGL“ genannt'),
      absatz('und'),
      absatz(
        [
          platzhalter('ANREDE'),
          zeilenumbruch(),
          platzhalter('VORNAME'),
          text(' '),
          platzhalter('NACHNAME'),
          zeilenumbruch(),
          platzhalter('STRASSE'),
          zeilenumbruch(),
          platzhalter('PLZ'),
          text(' '),
          platzhalter('ORT'),
        ],
        'vertragspartner',
      ),
      absatz('nachfolgend „Berater“ genannt'),
      nummer(
        1,
        'Vertragsgegenstand',
        'Gegenstand dieses Vertrags ist die Zusammenarbeit zwischen MUGL und dem Berater bei der Vermittlung von Reisen und touristischen Leistungen. Der Berater ist selbständig tätig, bestimmt Ort und Zeit seiner Tätigkeit selbst und trägt sein unternehmerisches Risiko. Eine Anstellung wird durch diesen Vertrag nicht begründet.',
      ),
      nummer(
        2,
        'Stellung des Beraters',
        'Der Berater handelt als selbständiger Handelsvertreter. Für die Einhaltung steuerlicher und sozialversicherungsrechtlicher Pflichten ist allein der Berater verantwortlich. Er ist nicht berechtigt, MUGL rechtsgeschäftlich zu vertreten oder Zusagen im Namen von MUGL abzugeben. Beschäftigt der Berater eigene Mitarbeiter, informiert er diese über die Vereinbarungen dieses Vertrags und achtet auf deren Einhaltung.',
      ),
      nummer(
        3,
        'Tätigkeit des Beraters',
        'Der Berater vermittelt Reiseverträge und touristische Dienstleistungen für MUGL. Eigene Agenturverträge mit Veranstaltern, mit denen MUGL unmittelbar zusammenarbeitet, schließt der Berater nur nach vorheriger Abstimmung. Vereinbarungen mit sonstigen Leistungsträgern wie Fluggesellschaften, Busunternehmen, Fähranbietern oder Hoteldatenbanken kann der Berater ohne Abstimmung eingehen.',
      ),
      nummer(
        4,
        'Reiseverträge',
        'Reiseverträge kommen durch Vermittlung des Beraters zwischen Veranstalter beziehungsweise Leistungsträger und Kunde zustande. Der Berater lässt sich jede Buchung vom Kunden bestätigen und bewahrt die Unterlagen nach den handelsrechtlichen Vorschriften auf; Bestätigung und Archivierung sind auch digital möglich. Er weist seine Kunden auf die Geschäftsbedingungen des jeweiligen Veranstalters hin, klärt über Einreisebestimmungen auf und empfiehlt den Abschluss geeigneter Reiseversicherungen.',
      ),
      nummer(
        5,
        'Vergütung',
        'Der Berater erhält für vermittelte Leistungen eine Provision nach der jeweils gültigen Provisionsübersicht von MUGL. Die Abrechnung erstellt MUGL monatlich als Gutschrift. Die Auszahlung erfolgt jeweils im Monat nach der Abreise auf das vom Berater benannte Konto. Provisionen für stornierte oder nicht angetretene Reisen werden entsprechend der Veranstalterabrechnung korrigiert.',
      ),
      nummer(
        6,
        'Beginn, Laufzeit und Kündigung',
        'Der Vertrag beginnt am vereinbarten Startdatum und läuft auf unbestimmte Zeit. Beide Seiten können ihn mit einer Frist von drei Monaten zum Monatsende kündigen. Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Nach Vertragsende erstellt der Berater eine Aufstellung der noch offenen Buchungen zur abschließenden Abrechnung.',
      ),
      absatz([
        text('Beraternummer: '),
        platzhalter('BERATERNUMMER'),
        text(', Startdatum: '),
        platzhalter('STARTDATUM'),
        text(', Vertragsinhaber: '),
        platzhalter('VERTRAGSINHABER'),
        text(', Firma: '),
        platzhalter('FIRMA'),
      ]),
      nummer(
        7,
        'Abwicklung der Buchungen',
        'Die Abwicklung und Weiterbearbeitung getätigter Buchungen erfolgt zentral durch das Buchungsmanagement von MUGL. Für eine zügige Bearbeitung legt MUGL Standards und Abläufe fest, die der Berater einhält. Entstehen für besondere Leistungen zusätzliche Kosten, werden diese dem Berater rechtzeitig vorher mitgeteilt.',
      ),
      nummer(
        8,
        'Nutzung der Marke',
        'Die Nutzung der Marke „Mein Urlaubsglück“ ist dem Berater während der Vertragslaufzeit gestattet. Die Marke darf nur in der von MUGL vorgegebenen Gestaltung verwendet werden. Eigene Rechte an der Marke oder ihren Bestandteilen erwirbt der Berater nicht; sie verbleiben vollständig beim Markeninhaber.',
      ),
      nummer(
        9,
        'Datenschutz und Vertraulichkeit',
        'Der Berater verarbeitet Kundendaten ausschließlich rechtmäßig, nachvollziehbar und mit angemessener Sicherheit. Für Werbemaßnahmen von MUGL benötigt er eine wirksame Einwilligung des Kunden. Geschäftliche Informationen von MUGL behandelt der Berater vertraulich, auch über das Vertragsende hinaus.',
      ),
      liste([
        'Zugangsdaten zu Buchungssystemen sind persönlich und werden nicht weitergegeben.',
        'Kundendaten werden nur für die Vermittlung und Betreuung verwendet.',
        'Sicherheitsvorfälle meldet der Berater unverzüglich an MUGL.',
      ]),
      nummer(
        10,
        'Schlussbestimmungen',
        'Änderungen und Ergänzungen dieses Vertrags bedürfen der Textform. Sollte eine Bestimmung unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt; an die Stelle der unwirksamen Bestimmung tritt eine Regelung, die dem wirtschaftlichen Zweck am nächsten kommt. Gerichtsstand ist, soweit zulässig, der Sitz von MUGL.',
      ),
      absatz([]),
      absatz([text('Ort, Datum: ______________________________')]),
      absatz([text('Unterschrift Berater: ______________________________')]),
      absatz([text('Mein Urlaubsglück GmbH: ______________________________')]),
      seitenumbruch('umbruch-anlage'),
      absatz([text('ANLAGE 1', { fett: true })], 'anlage-titel'),
      absatz([text('1. Erklärung zur Umsatzsteuer für meine Provisionsgutschrift', { fett: true })]),
      absatz([text('Steuernummer: '), platzhalter('STEUERNUMMER')]),
      absatz([text('USt.-ID: ______________________________')]),
      absatz('Bitte ankreuzen:'),
      absatz([
        kaestchen(false),
        text('Ich unterliege der Regelbesteuerung und rechne die ausgezahlte Umsatzsteuer mit dem Finanzamt ab.'),
      ]),
      absatz([
        kaestchen(false),
        text('Ich unterliege der Kleinunternehmerregelung und erhalte meine Gutschriften netto ohne Umsatzsteuer.'),
      ]),
      absatz([text('2. SEPA-Lastschriftmandat', { fett: true })]),
      absatz([text('Kontoinhaber: '), platzhalter('VORNAME'), text(' '), platzhalter('NACHNAME')]),
      absatz([text('Name der Bank: '), platzhalter('BANKNAME')]),
      absatz([text('IBAN: '), platzhalter('IBAN')]),
      absatz([text('BIC: '), platzhalter('BIC')]),
      absatz([text('Mandatsreferenz: '), platzhalter('MANDATSREFERENZ')]),
      absatz([
        kaestchen(true),
        text('Ich ermächtige MUGL, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.'),
      ]),
      absatz([]),
      absatz([text('Ort, Datum: ______________________________')]),
      absatz([text('Unterschrift Berater: ______________________________')]),
    ],
  });
}

export default beispielvertrag;
